import React, { useRef, useState, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useGameLoop } from '../Shared/hooks/useGameLoop';
import GameLayout from '../Shared/components/GameLayout/GameLayout';
import Scoreboard from '../Shared/components/Scoreboard/Scoreboard';
import PropTypes from 'prop-types';
import Countdown from '../Shared/components/Countdown/Countdown';
import { useAudio } from '../Shared/hooks/useAudio';
import { GAME_THEME } from '../Shared/constants/GameTheme';

export const calculateLeadPosition = (phi, omega, lag, radius) => {
    // Relative prediction in rotated space
    // Future angle relative to current rotation = phi + omega * lag
    const leadAngle = phi + omega * lag;
    return {
        x: Math.cos(leadAngle) * radius,
        y: Math.sin(leadAngle) * radius
    };
};

const RotatingStar = () => {
    const canvasRef = useRef(null);
    const canvasSizeRef = useRef({ width: 0, height: 0 }); // Track size for centering
    const { beep, ping } = useAudio();

    // Physics Constants
    const G = 800;
    const R = 170;
    const TARGET_MASS = 1;
    const HUB_MASS = 2;
    const BEARING_DRAG = 0.9998;
    const COUNT = 5;

    // Game State Ref (Physics)
    const stateRef = useRef({
        theta: 0,
        omega: 0,
        running: true,
        startTime: null,
        targets: [],
        completed: false,
        hits: 0,
        misses: 0
    });


    // UI State
    const [currentTime, setCurrentTime] = useState(0);
    const [uiStats, setUiStats] = useState({ hits: 0, misses: 0 });
    const [finishedRank, setFinishedRank] = useState(null); // Rank of last run
    const [bestTime, setBestTime] = useState(null);
    const [highScores, setHighScores] = useState([]); // Assuming we want a list like others
    const [inputLag, setInputLag] = useState(0); // Default 0 or 250? User asked for slider. Let's start at 0 or 250. LeadGame uses 250.
    const [targetRadius, setTargetRadius] = useState(15);

    const [isCountingDown, setIsCountingDown] = useState(false);
    const [showStartModal, setShowStartModal] = useState(true);
    const [calibratedVal, setCalibratedVal] = useState(null);
    const [prepMode, setPrepMode] = useState('off');



    const loadScores = useCallback(() => {
        const list = JSON.parse(localStorage.getItem("rotatingStarScores") || "[]");
        setHighScores(list);
    }, []);

    const saveRun = (time, hits, misses) => {
        const list = JSON.parse(localStorage.getItem("rotatingStarScores") || "[]");

        // Efficiency score
        const efficiency = hits / time;

        const sizeLabel = { 10: "Small", 15: "Normal", 25: "Large", 35: "Huge" }[targetRadius] || targetRadius;
        list.push({ time, hits, misses, efficiency, size: sizeLabel, date: new Date().toISOString() });
        // Sort by Efficiency (Descending)
        list.sort((a, b) => b.efficiency - a.efficiency);

        // Find rank (1-based)
        const rank = list.findIndex(x => x.time === time && x.hits === hits && x.misses === misses) + 1;
        setFinishedRank(rank);

        const top = list.slice(0, 10);
        localStorage.setItem("rotatingStarScores", JSON.stringify(top));
        setHighScores(top);

        const best = top.length > 0 ? top[0].time : null;
        setBestTime(best);
        if (best) localStorage.setItem("rotatingStarBest", best);
    };

    const clearScores = useCallback(() => {
        localStorage.removeItem("rotatingStarScores");
        localStorage.removeItem("rotatingStarBest");
        setHighScores([]);
        setBestTime(null);
    }, []);

    const actualStart = useCallback(() => {
        beep(1000, 0.2); // Start signal
        const state = stateRef.current;
        state.running = true;
        state.startTime = performance.now();
    }, [beep]);

    // Initialize/Reset Logic
    const reset = useCallback(() => {
        const state = stateRef.current;
        state.theta = 0;
        state.omega = 0;
        state.running = false; // Stay paused during countdown
        state.startTime = null;
        state.completed = false;
        state.targets = [];
        state.hits = 0;
        state.misses = 0;
        setUiStats({ hits: 0, misses: 0 });
        setFinishedRank(null);

        for (let i = 0; i < COUNT; i++) {
            state.targets.push({
                phi: (i * 2 * Math.PI) / COUNT,
                attached: true,
                y: 0
            });
        }
        setCurrentTime(0);
    }, [COUNT]);

    const stageStart = useCallback(() => {
        setFinishedRank(null);
        setShowStartModal(false);
        setIsCountingDown(true);
    }, []);

    const startGame = useCallback(() => {
        reset();
        stageStart();
    }, [reset, stageStart]);

    // Load High Scores / Best
    useEffect(() => {
        reset();
        loadScores();
        const savedBest = localStorage.getItem("rotatingStarBest");
        if (savedBest) setBestTime(Number(savedBest));

        const cal = localStorage.getItem('calibratedLatency');
        if (cal) {
            const val = Number(cal);
            setCalibratedVal(val);
            setInputLag(val); // Auto-apply
        }
    }, [reset, loadScores]);

    // Game Loop
    const loop = useCallback((now, dt) => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        const state = stateRef.current;

        // Update Canvas Size Ref for click handler
        canvasSizeRef.current = { width: canvas.width, height: canvas.height };

        const cx = canvas.width / 2;
        const cy = canvas.height / 2;

        // Physics Update
        if (state.running && !state.completed && state.startTime) {
            let torque = 0;
            let I = HUB_MASS * R * R;

            for (const p of state.targets) {
                if (p.attached) {
                    const a = state.theta + p.phi;
                    torque += TARGET_MASS * G * R * Math.cos(a);
                    I += TARGET_MASS * R * R;
                }
            }

            const alpha = torque / I;
            state.omega += alpha * dt;
            // Time-corrected drag: (0.9998 / 16.6ms) -> pow(0.9998, dt / 0.01666)
            state.omega *= Math.pow(BEARING_DRAG, dt / 0.01666);
            state.theta += state.omega * dt;

            // Update timer display
            if (state.startTime) {
                setCurrentTime((performance.now() - state.startTime) / 1000);
            }
        }

        // Drop detached targets
        for (const p of state.targets) {
            if (!p.attached) {
                p.y += 600 * dt;
            }
        }

        // Draw
        // ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = GAME_THEME.BACKGROUND_COLOR;
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        ctx.save();
        ctx.translate(cx, cy);

        // Draw Hub & Arms
        ctx.save();
        ctx.rotate(state.theta);
        ctx.strokeStyle = "#777";
        ctx.lineWidth = 4;
        for (const p of state.targets) {
            ctx.beginPath();
            ctx.moveTo(0, 0);
            ctx.lineTo(Math.cos(p.phi) * R, Math.sin(p.phi) * R);
            ctx.stroke();
        }
        ctx.beginPath();
        ctx.arc(0, 0, 20, 0, Math.PI * 2);
        ctx.fillStyle = "#aaa";
        ctx.fill();

        // Draw Attached Nodes
        for (const p of state.targets) {
            if (!p.attached) continue;
            ctx.beginPath();
            ctx.arc(Math.cos(p.phi) * R, Math.sin(p.phi) * R, targetRadius, 0, Math.PI * 2);
            ctx.fillStyle = GAME_THEME.TARGET_FILL;
            ctx.fill();
            ctx.strokeStyle = GAME_THEME.TARGET_STROKE;
            ctx.lineWidth = GAME_THEME.TARGET_STROKE_WIDTH;
            ctx.stroke();

            // Lead/Latency Indicator
            if (inputLag > 0) {
                const lag = inputLag / 1000;
                // Context is already rotated by theta.
                // WE calculate lead offset relative to the plate's attachment angle (phi).
                const pos = calculateLeadPosition(p.phi, state.omega, lag, R);

                ctx.strokeStyle = "#2ecc71";
                ctx.lineWidth = 2;
                ctx.beginPath();
                // Draw an 'X' shape
                const s = 8; // size
                ctx.moveTo(pos.x - s, pos.y - s); ctx.lineTo(pos.x + s, pos.y + s);
                ctx.moveTo(pos.x + s, pos.y - s); ctx.lineTo(pos.x - s, pos.y + s);
                ctx.stroke();
            }
        }
        ctx.restore();

        // Draw Detached Nodes (Global coordinates relative to center, but not rotated)
        for (const p of state.targets) {
            if (!p.attached) {
                ctx.beginPath();
                ctx.arc(
                    cx + Math.cos(state.theta + p.phi) * R,
                    cy + p.y,
                    targetRadius,
                    0,
                    Math.PI * 2
                );
                ctx.fillStyle = GAME_THEME.TARGET_FILL;
                ctx.fill();
                ctx.strokeStyle = GAME_THEME.TARGET_STROKE;
                ctx.lineWidth = GAME_THEME.TARGET_STROKE_WIDTH;
                ctx.stroke();
            }
        }

        ctx.restore();

    }, [reset, inputLag, targetRadius]); // Added inputLag to dependency

    useGameLoop(loop, true);

    const handleCanvasClick = (e) => {
        const state = stateRef.current;
        if (!state.running || state.completed) return;

        // Start timer on first shot? Original starts on click.
        if (!state.startTime) state.startTime = performance.now();

        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        const cx = canvas.width / 2;
        const cy = canvas.height / 2;
        const mx = e.clientX - rect.left - cx;
        const my = e.clientY - rect.top - cy;

        let hitFound = false;
        for (const p of state.targets) {
            if (!p.attached) continue;

            const a = state.theta + p.phi;
            const px = Math.cos(a) * R;
            const py = Math.sin(a) * R;

            if (Math.hypot(mx - px, my - py) < (targetRadius + 5)) {
                hitFound = true;
                p.attached = false;
                p.y = py;
                state.hits++;
                ping(); // Play steel ping sound

                // Check win
                if (state.targets.every(pl => !pl.attached)) {
                    state.completed = true;
                    // Ensure hits is up to date before saving
                    // We incremented hits just before this block in Step 540?
                    // Let's look at Step 540:
                    // hitFound = true; p.attached = false; p.y = py; state.hits++;
                    // Check win ...
                    // So hits IS incremented.
                    const finalTime = (performance.now() - state.startTime) / 1000;
                    setCurrentTime(finalTime);
                    saveRun(finalTime, state.hits, state.misses);
                }
                break;
            }
        }

        if (!hitFound) {
            state.misses++;
        }
        setUiStats({ hits: state.hits, misses: state.misses });
    };

    const sidebarContent = (
        <>
            <div style={{ marginBottom: '20px' }}>
                <h3 style={{ marginTop: 0 }}>Rotating Star</h3>
                <p style={{ fontSize: '12px', color: '#aaa' }}>
                    Physics-based rotating game. Click the targets to remove them and unbalance the star.
                </p>
            </div>

            <label>Target Size</label>
            <select style={{ display: 'block', marginBottom: '15px' }} value={targetRadius} onChange={(e) => setTargetRadius(Number(e.target.value))}>
                <option value={10}>Small (10px)</option>
                <option value={15}>Normal (15px)</option>
                <option value={25}>Large (25px)</option>
                <option value={35}>Huge (35px)</option>
            </select>


            <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span>Input Lag: <span>{inputLag}</span>ms</span>
                {calibratedVal !== null ? (
                    <button
                        onClick={() => setInputLag(calibratedVal)}
                        style={{
                            background: 'none', border: '1px solid #f1c40f', color: '#f1c40f',
                            borderRadius: '4px', cursor: 'pointer', fontSize: '10px', padding: '2px 5px',
                            display: 'flex', alignItems: 'center', gap: '4px'
                        }}
                        title={`Use calibrated value: ${calibratedVal}ms`}
                    >
                        <span>★</span> USE {calibratedVal}ms
                    </button>
                ) : (
                    <Link to="/test-latency" style={{ fontSize: '10px', color: '#aaa', textDecoration: 'underline' }}>
                        Calibrate?
                    </Link>
                )}
            </label>
            <input
                type="range"
                min="0"
                max="500"
                value={inputLag}
                onChange={(e) => setInputLag(Number(e.target.value))}
            />


            <button onClick={reset}>Reset Game</button>

            <button onClick={startGame}>Start Game</button>

            <Scoreboard
                stats={{
                    "Time": currentTime.toFixed(2) + "s",
                    "Hits": uiStats.hits,
                    "Misses": uiStats.misses,
                    "Best": bestTime ? bestTime.toFixed(2) + "s" : "--"
                }}
                highScores={highScores}
                onReset={clearScores}
                title="Top Times"
            />

            <label>Prep Mode</label>
            <select value={prepMode} onChange={(e) => setPrepMode(e.target.value)}>
                <option value="off">Off</option>
                <option value="on">On (Delay)</option>
            </select>
            {/* Show simple best time separately or in stats above */}
        </>
    );

    const handleCountdownFinish = useCallback(() => {
        setIsCountingDown(false);
        actualStart();
    }, [actualStart]);

    return (
        <GameLayout
            sidebarContent={sidebarContent}
            canvasRef={canvasRef}
            onCanvasClick={handleCanvasClick}
            gameRunning={(stateRef.current.startTime !== null && finishedRank === null) || isCountingDown}
            onQuit={() => {
                stateRef.current.running = false;
                stateRef.current.startTime = null; // Reset startTime to stop timer
                setCurrentTime(0); // Reset UI timer
                setUiStats({ hits: 0, misses: 0 }); // Reset UI stats
                setIsCountingDown(false);
                setShowStartModal(true);
                reset(); // Ensure full game state reset
            }}
        >
            {isCountingDown && (
                <Countdown prepMode={prepMode === 'on'} onFinish={handleCountdownFinish} />
            )}
            {showStartModal && (
                <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    background: 'rgba(0, 0, 0, 0.7)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10
                }}>
                    <div style={{
                        background: '#222',
                        padding: '50px',
                        borderRadius: '16px',
                        textAlign: 'center',
                        border: '1px solid #444',
                        color: '#fff',
                        boxShadow: '0 15px 35px rgba(0,0,0,0.6)',
                        minWidth: '350px'
                    }}>
                        <h1 style={{ marginTop: 0, color: '#f1c40f', fontSize: '2.5em', marginBottom: '10px' }}>Rotating Star</h1>
                        <p style={{ color: '#ccc', marginBottom: '30px', fontSize: '1.1em' }}>
                            Click all 5 targets as fast as possible.<br />
                            The star will rotate as targets are removed!
                        </p>
                        <button
                            onClick={startGame}
                            style={{
                                padding: '16px 40px', fontSize: '20px', fontWeight: 'bold',
                                background: GAME_THEME.BUTTON_BG, color: 'white', border: '1px solid #444', borderRadius: '8px', cursor: 'pointer',
                                transition: 'all 0.2s', margin: '0 auto'
                            }}
                        >
                            START GAME
                        </button>
                    </div>
                </div>
            )}
            {finishedRank !== null && (
                <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    background: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10
                }}>
                    <div style={{
                        background: '#222',
                        padding: '40px',
                        borderRadius: '12px',
                        textAlign: 'center',
                        border: '1px solid #444',
                        color: '#fff',
                        boxShadow: '0 10px 25px rgba(0,0,0,0.5)',
                        minWidth: '300px'
                    }}>
                        <h2 style={{ marginTop: 0, color: '#f1c40f', fontSize: '2em' }}>Finished!</h2>

                        <div style={{ margin: '20px 0', fontSize: '1.2em' }}>
                            <div style={{ fontSize: '2.5em', fontWeight: 'bold', marginBottom: '10px' }}>
                                {currentTime.toFixed(2)}s
                            </div>
                            <div style={{ color: '#aaa', fontSize: '0.9em' }}>
                                Efficiency: {(uiStats.hits / currentTime).toFixed(4)}
                            </div>
                            <div style={{ color: '#aaa', fontSize: '0.9em' }}>
                                Size: {{ 10: "Small", 15: "Normal", 25: "Large", 35: "Huge" }[targetRadius] || targetRadius}
                            </div>
                        </div>

                        <div style={{
                            background: '#333',
                            padding: '10px',
                            borderRadius: '5px',
                            marginBottom: '25px',
                            border: '1px solid #555'
                        }}>
                            You placed <strong style={{ color: '#2ecc71', fontSize: '1.2em' }}>#{finishedRank}</strong> on the scoreboard!
                        </div>

                        <button
                            onClick={startGame}
                            style={{
                                padding: '12px 30px', fontSize: '18px', fontWeight: 'bold',
                                background: GAME_THEME.BUTTON_BG, color: 'white', border: '1px solid #444', borderRadius: '6px',
                                cursor: 'pointer', transition: 'background 0.2s', margin: '0 auto'
                            }}
                        >
                            Play Again
                        </button>
                    </div>
                </div>
            )}
        </GameLayout>
    );
};

export default RotatingStar;
